import {
    ACTION_TYPES,
    MIN_SWIMLANE_ROWS,
    TIMELINE_ROW_HEIGHT,
    TRANSITION_DURATION_MS,
} from '@/roadmaps/constants/roadmaps.constants';
import { calculateItemHeight, getElementPadding } from '@/roadmaps/helpers/roadmaps-layout.helper';
import { waitForTransition } from '@/roadmaps/helpers/roadmaps.helper';
import { useDraggable } from '@/roadmaps/use/use-draggable';
import { getPendingActionById } from '@/roadmaps/use/use-pending-actions';
import useScroll from '@/roadmaps/use/use-scroll';
/* eslint-disable no-restricted-imports */
/* eslint-disable internal-rules/no-store-in-composables */
import store from '@/vuex/store';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import findKey from 'lodash/findKey';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import pick from 'lodash/pick';
import sum from 'lodash/sum';
import Vue, { nextTick, reactive, unref } from 'vue';

let swimlaneRowHeights;
let pendingSwimlaneRowHeights;
let swimlaneRefAndBounds;

export function initUseSwimlanes () {
    swimlaneRefAndBounds = reactive({});
    swimlaneRowHeights = reactive({});
    pendingSwimlaneRowHeights = reactive({});

    updateUseSwimlanes();
}

export function updateUseSwimlanes (swimlaneIds = store.getters['roadmaps/swimlanes/swimlaneIds']) {
    each(swimlaneIds, (swimlaneId) => {
        if (!swimlaneRefAndBounds[swimlaneId]) {
            addSwimlane(swimlaneId);
        }
    });

    each(swimlaneRefAndBounds, (__, swimlaneId) => {
        if (!swimlaneIds.includes(swimlaneId)) {
            removeSwimlane(swimlaneId);
        }
    });
}

function addSwimlane (swimlaneId) {
    const swimlane = {
        ref: null,
        bounds: {},
    };
    const swimlaneBody = {
        ref: null,
        bounds: {},
    };
    Vue.set(swimlaneRefAndBounds, swimlaneId, { swimlane, swimlaneBody });
    calculateSwimlaneRowHeights(swimlaneId);
    Vue.set(pendingSwimlaneRowHeights, swimlaneId, []);
}

function removeSwimlane (swimlaneId) {
    delete swimlaneRefAndBounds[swimlaneId];
    delete pendingSwimlaneRowHeights[swimlaneId];
    delete swimlaneRowHeights[swimlaneId];
}

export async function resetUseSwimlanes ({ waitForTransitionsToFinish = false } = {}) {
    if (waitForTransitionsToFinish) await waitForTransition();
    const swimlaneIds = store.getters['roadmaps/swimlanes/swimlaneIds'];
    each(swimlaneIds, (swimlaneId) => {
        calculateSwimlaneRowHeights(swimlaneId);
    });
    calculateAllSwimlaneBounds();
}

export function resetPendingSwimlaneRowHeights () {
    const swimlaneIds = store.getters['roadmaps/swimlanes/swimlaneIds'];
    each(swimlaneIds, (swimlaneId) => {
        Vue.set(pendingSwimlaneRowHeights, swimlaneId, []);
    });
}

function safeSetRef (swimlaneId, prop, ref) {
    if (!swimlaneRefAndBounds[swimlaneId]) {
        addSwimlane(swimlaneId);
    }

    Vue.set(swimlaneRefAndBounds[swimlaneId][prop], 'ref', ref);
}

export function setSwimlaneRef (swimlaneId, swimlaneRef) {
    safeSetRef(swimlaneId, 'swimlane', swimlaneRef);
}

export function setSwimlaneBodyRef (swimlaneId, swimlaneBodyRef) {
    safeSetRef(swimlaneId, 'swimlaneBody', swimlaneBodyRef);
}

export function calculateAllSwimlaneBounds () {
    nextTick(() => {
        each(swimlaneRefAndBounds, (__, swimlaneId) => {
            const swimlaneBounds = swimlaneRefAndBounds[swimlaneId].swimlane.ref?.getBoundingClientRect();
            if (swimlaneBounds) {
                calculateSwimlaneBounds(swimlaneId);
                calculateSwimlaneBodyBounds(swimlaneId);
            }
        });
    });
}

export function calculateSwimlaneBounds (swimlaneId) {
    const swimlaneBounds = swimlaneRefAndBounds[swimlaneId].swimlane.ref.getBoundingClientRect();
    Vue.set(swimlaneRefAndBounds[swimlaneId].swimlane, 'bounds', swimlaneBounds);
}

export function calculateSwimlaneBodyBounds (swimlaneId) {
    nextTick(() => {
        const refSwimlaneBody = swimlaneRefAndBounds[swimlaneId].swimlaneBody.ref;
        const swimlaneBodyBounds = pick(refSwimlaneBody.getBoundingClientRect(), [
            'top',
            'right',
            'bottom',
            'left',
            'height',
            'width',
        ]);
        const elementPadding = getElementPadding(refSwimlaneBody);
        const { scrollWidth } = refSwimlaneBody;
        const { scrollPosition } = useScroll();
        swimlaneBodyBounds.innerTop = swimlaneBodyBounds.top + elementPadding.top + scrollPosition.top;
        swimlaneBodyBounds.innerLeft = swimlaneBodyBounds.left + elementPadding.left;
        swimlaneBodyBounds.scrollWidth = scrollWidth;
        swimlaneBodyBounds.innerScrollWidth = scrollWidth - (elementPadding.left + elementPadding.right);
        Vue.set(swimlaneRefAndBounds[swimlaneId].swimlaneBody, 'bounds', swimlaneBodyBounds);
    });
}

export function getSwimlaneBounds (swimlaneId) {
    return swimlaneRefAndBounds[swimlaneId].swimlane.bounds;
}

export function getSwimlaneBodyBounds (swimlaneId) {
    return swimlaneRefAndBounds[swimlaneId].swimlaneBody.bounds;
}

export function getSwimlaneIdFromY (y, fallbackSwimlaneId = null) {
    const swimlaneId = findKey(
        swimlaneRefAndBounds,
        (refAndBounds) => y >= refAndBounds.swimlane.bounds.top && y <= refAndBounds.swimlane.bounds.bottom,
    );

    return swimlaneId || fallbackSwimlaneId;
}

export function calculateSwimlaneRowHeights (swimlaneId) {
    const swimlaneItems = store.getters['roadmaps/items/itemsByParentId'](swimlaneId);
    const rowHeights = calculateRowHeightsFromSwimlaneItems(swimlaneItems);

    // Ensure there is always an empty row at the end
    rowHeights.push(TIMELINE_ROW_HEIGHT);

    // If the number of rows is less than MIN_SWIMLANE_ROWS pad to fill
    if (rowHeights.length < MIN_SWIMLANE_ROWS) {
        const numExtraRows = MIN_SWIMLANE_ROWS - rowHeights.length;
        const extraRows = Array(numExtraRows).fill(TIMELINE_ROW_HEIGHT);
        rowHeights.push(...extraRows);
    }

    setSwimlaneRowHeights(swimlaneId, rowHeights);
}

export function calculateAllPendingSwimlaneRowHeights () {
    const swimlaneIds = store.getters['roadmaps/swimlanes/swimlaneIds'];
    const { draggedItem } = useDraggable();

    each(swimlaneIds, (id) => {
        calculatePendingSwimlaneRowHeights(id, unref(draggedItem));
    });
}

function calculatePendingSwimlaneRowHeights (swimlaneId, draggedItem) {
    const swimlaneItems = cloneDeep(store.getters['roadmaps/items/itemsByParentId'](swimlaneId));
    const items = [];

    each(swimlaneItems, (item) => {
        if (item.id === draggedItem?.id) return;

        let itemWithUpdates = item;
        const pendingAction = getPendingActionById(item.id);
        if (pendingAction.type === ACTION_TYPES.RESOLVE && pendingAction.preview) {
            itemWithUpdates = Object.assign({}, item, pendingAction.updates);
        }
        items.push(itemWithUpdates);
    });

    const rowHeights = calculateRowHeightsFromSwimlaneItems(items);
    Vue.set(pendingSwimlaneRowHeights, swimlaneId, rowHeights);
}

function calculateRowHeightsFromSwimlaneItems (swimlaneItems) {
    const itemsGroupedByRow = groupBy(swimlaneItems, 'row');

    const rowsWithItems = Object.keys(itemsGroupedByRow);
    const lastRowWithItems = parseInt(rowsWithItems[rowsWithItems.length - 1]);

    const rowHeights = [];
    for (let i = 0; i <= lastRowWithItems; i++) {
        if (!itemsGroupedByRow[i]) {
            rowHeights.push(TIMELINE_ROW_HEIGHT);
        } else {
            rowHeights.push(calculateMaxRowHeightFromRowItems(itemsGroupedByRow[i]));
        }
    }

    return rowHeights;
}

function calculateMaxRowHeightFromRowItems (rowItems) {
    return Math.max(
        ...map(rowItems, (item) => {
            return calculateItemHeight(item);
        }),
    );
}

export function recalculateRowHeightAfterExpandCollapse (swimlaneId, row) {
    const swimlaneItemsInRow = cloneDeep(store.getters['roadmaps/items/swimlaneItemsByRow'](swimlaneId, row));
    const newRowHeights = [...swimlaneRowHeights[swimlaneId]];
    newRowHeights[row] = calculateMaxRowHeightFromRowItems(swimlaneItemsInRow);
    setSwimlaneRowHeights(swimlaneId, newRowHeights);

    // Wait for transition to complete
    const transitionBuffer = 50;
    setTimeout(() => {
        calculateAllSwimlaneBounds();
    }, TRANSITION_DURATION_MS + transitionBuffer);
}

function setSwimlaneRowHeights (swimlaneId, rowHeights) {
    Vue.set(swimlaneRowHeights, swimlaneId, rowHeights);
}

export function getSwimlaneBodyHeight (swimlaneId) {
    /**
     * Added +1 to account for the  Border Top from the TimelineSwimlaneBody
     */
    return sum(swimlaneRowHeights[swimlaneId]) + 1;
}

export function getRowFromY (swimlaneId, y) {
    let cumulativeRowHeight = 0;

    for (let index = 0; index < swimlaneRowHeights[swimlaneId].length; index++) {
        cumulativeRowHeight += swimlaneRowHeights[swimlaneId][index];
        if (y <= cumulativeRowHeight) return index;
    }

    return swimlaneRowHeights[swimlaneId].length ? swimlaneRowHeights[swimlaneId].length - 1 : 0;
}

export function getSubRowFromY (item, y) {
    const children = store.getters['roadmaps/items/childItemsWithoutDraggedItem'](item.id);
    if (!item.isExpanded) return 0;

    const rowTop = getRowTop(item.parentId, item.row);
    const yRelativeToItem = y - rowTop;
    if (yRelativeToItem < TIMELINE_ROW_HEIGHT) {
        return 0;
    }

    let cumulativeSubRowHeight = TIMELINE_ROW_HEIGHT;
    for (let index = 0; index < children.length; index++) {
        cumulativeSubRowHeight += TIMELINE_ROW_HEIGHT;
        if (yRelativeToItem <= cumulativeSubRowHeight) return index;
    }

    return children.length;
}

export function getRowTop (swimlaneId, row) {
    return sum(swimlaneRowHeights[swimlaneId].slice(0, row));
}

export function getPendingRowTop (swimlaneId, row) {
    if (isEmpty(pendingSwimlaneRowHeights[swimlaneId])) return;

    return sum(pendingSwimlaneRowHeights[swimlaneId].slice(0, row));
}

export function getSwimlaneBodyWidth () {
    const { innerScrollWidth } = swimlaneRefAndBounds[Object.keys(swimlaneRefAndBounds)[0]].swimlaneBody.bounds;

    return innerScrollWidth;
}
